import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import keys from 'constants/keys'
import { isRtlSelector } from 'redux/selectors/general'
import { Camera, Video, AlertCircle, MessageSquare } from 'lucide-react'
import { useExecutionMedia } from '../../hooks/useExecutionMedia'
import { Button, MediaUploadPreview } from 'components/ui'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { IMediaSubtaskProps } from '../../Content/MediaSubtasksCompleting/MediaSubtaskCompleting'
import { ExecutionGallery } from '../ExecutionGallery'
import { ExecutionDescription } from '../ExecutionDescription'
import { useExecutionText } from '../../hooks/useExecutionText'
import { ResultCommments } from 'components/TasksV2/CommentsSection/ResultCommments'
import { EmptyReviewState } from '../DashboardReview/EmptyReviewState'
import { CameraCapture } from 'components/ui/CameraCapture'
import { ExecutionReviewFeedback } from '../ExecutionReviewFeedback'

export const ExecutionMediaSubtask: React.FC<IMediaSubtaskProps> = ({
  embedded,
  images,
  videos,
  subtask,
  resultContextId,
  isReadMode,
  mediaType,
  currentMediaIndex = 0,
  onMediaChange,
  mediaFooter,
  mediaHeader,
  reviewFeedback,
  retail_id_context_id,
  recurrence_ts,
  tid,
  isReworkLimitReached,
  targetParentCommentId,
  targetCommentId,
}) => {
  const t = useExecutionText()
  const isRtl = useSelector(isRtlSelector)
  const [imageToComment, setImageToComment] = useState<string>()
  const [composerTarget, setComposerTarget] = useState<HTMLDivElement | null>(null)
  const [commentOverlayOpen, setCommentOverlayOpen] = useState(false)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)
  const {
    taskSubtask,
    result,
    media,
    index,
    currentUrl,
    counts,
    setSelected,
    upload,
    navigationDisabled,
    interactionBlocked,
    analysisLoading,
    analysisError,
    saveError,
    analyze,
    addFiles,
    retry,
    deleteCurrent,
    score,
    canAdd,
    isImage,
  } = useExecutionMedia({
    images,
    videos,
    subtask,
    resultContextId,
    isReadMode,
    mediaType,
    currentMediaIndex,
    onMediaChange,
    retail_id_context_id,
    recurrence_ts,
    tid,
    isReworkLimitReached,
  })
  const pendingUpload = upload.queue.find((item) => item.state === 'uploading') || upload.queue[0]
  const captureOnly = taskSubtask?.disable_gallery_upload ?? subtask.disable_gallery_upload
  const uploadAccess = useRef({ canAdd, interactionBlocked, captureOnly, addFiles })
  const mounted = useRef(true)

  uploadAccess.current = { canAdd, interactionBlocked, captureOnly, addFiles }

  const receiveFiles = useCallback((files: File[], source: 'capture' | 'gallery') => {
    const current = uploadAccess.current
    if (
      !mounted.current ||
      !current.canAdd ||
      current.interactionBlocked ||
      (source === 'gallery' && current.captureOnly)
    )
      return
    current.addFiles(files)
  }, [])

  const receiveGalleryFiles = useCallback((files: File[]) => receiveFiles(files, 'gallery'), [receiveFiles])

  const openGallery = () => {
    if (canAdd && !interactionBlocked && !captureOnly) fileInput.current?.click()
  }

  const openCapture = () => {
    if (!canAdd || interactionBlocked) return
    if (isImage) setIsCameraOpen(true)
    else cameraInput.current?.click()
  }

  const openDefaultPicker = captureOnly ? openCapture : openGallery

  const closeCamera = () => setIsCameraOpen(false)

  const receiveCameraPhoto = (file: File) => {
    closeCamera()
    receiveFiles([file], 'capture')
  }

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  useEffect(() => {
    if (interactionBlocked || !canAdd || !isImage) setIsCameraOpen(false)
  }, [canAdd, interactionBlocked, isImage])

  if (isReadMode && !media.length) return <EmptyReviewState />

  return (
    <div className={`execution-media-layout${embedded ? ' execution-media-embedded' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <ExecutionDescription subtask={taskSubtask || subtask} status={result?.status} notices={mediaHeader} />
      <div className="execution-media-result">
        <DropzoneWrapper
          className="execution-result-dropzone"
          disabled={interactionBlocked || !canAdd || captureOnly}
          onDrop={receiveGalleryFiles}
          onPaste={(e) => {
            if (canAdd && !interactionBlocked && !captureOnly && e.clipboardData.files.length) {
              e.preventDefault()
              receiveGalleryFiles(Array.from(e.clipboardData.files))
            }
          }}
        >
          {upload.busy && pendingUpload ? (
            <ExecutionGallery
              media={media}
              index={index}
              onChange={setSelected}
              counts={counts}
              isRtl={!!isRtl}
              onAdd={canAdd ? openDefaultPicker : undefined}
              addDisabled
              navigationDisabled
              stageOverride={
                <MediaUploadPreview
                  src={pendingUpload.preview}
                  mediaType={isImage ? 'image' : 'video'}
                  alt={pendingUpload.file.name}
                  previewLabel={t.uploading}
                  uploadingLabel={t.uploading}
                  uploading
                  previewDisabled
                  className="execution-pending-media"
                  previewClassName="execution-pending-media-preview"
                />
              }
            />
          ) : media.length ? (
            <ExecutionGallery
              media={media}
              index={index}
              onChange={setSelected}
              counts={counts}
              isRtl={!!isRtl}
              onAdd={canAdd ? openDefaultPicker : undefined}
              addDisabled={interactionBlocked}
              navigationDisabled={navigationDisabled}
              onDelete={!interactionBlocked ? deleteCurrent : undefined}
              onComment={() => setImageToComment(currentUrl)}
              aiScore={score}
              onComposerTarget={setComposerTarget}
              commentOverlayOpen={commentOverlayOpen}
            />
          ) : (
            <div className="execution-upload-empty">
              {isReadMode ? (
                <strong>{t.noMedia}</strong>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    className="execution-capture-title"
                    isDisabled={!canAdd || interactionBlocked}
                    onClick={openCapture}
                  >
                    {isImage ? t.takePhoto : t.record}
                  </Button>
                  {canAdd && (
                    <Button
                      type="button"
                      variant="icon"
                      className="execution-capture-button"
                      aria-label={captureOnly ? (isImage ? t.takePhoto : t.record) : t.upload}
                      isDisabled={interactionBlocked}
                      onClick={openDefaultPicker}
                    >
                      {isImage ? <Camera size={46} strokeWidth={1.9} /> : <Video size={46} strokeWidth={1.9} />}
                    </Button>
                  )}
                  {canAdd && !captureOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      activeColor="#737982"
                      className="execution-gallery-upload"
                      isDisabled={interactionBlocked}
                      onClick={openGallery}
                    >
                      {t.uploadFromGallery}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </DropzoneWrapper>
        {!upload.busy && upload.queue.length > 0 && (
          <div className="execution-upload-error" role="alert">
            <AlertCircle size={20} />
            <strong>{t.uploadFailed}</strong>
            <p>{t.uploadFailedHint}</p>
            {upload.queue.map((item) => (
              <div key={item.id}>
                <bdi>{item.file.name}</bdi>
                <Button variant="ghost" onClick={() => upload.remove(item.id)}>
                  {t.remove}
                </Button>
              </div>
            ))}
            <Button variant="primary" onClick={retry}>
              {t.retry}
            </Button>
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept={isImage ? 'image/*' : 'video/*'}
          multiple
          hidden
          onChange={(e) => {
            receiveGalleryFiles(Array.from(e.target.files || []))
            e.target.value = ''
          }}
        />
        {!isImage && (
          <input
            ref={cameraInput}
            type="file"
            accept="video/*"
            capture="environment"
            hidden
            onChange={(e) => {
              receiveFiles(Array.from(e.target.files || []), 'capture')
              e.target.value = ''
            }}
          />
        )}
        {isCameraOpen && isImage && canAdd && !interactionBlocked && (
          <CameraCapture onCapture={receiveCameraPhoto} onClose={closeCamera} />
        )}
        {reviewFeedback && <ExecutionReviewFeedback key={reviewFeedback.id} kind={reviewFeedback.kind} />}
      </div>
      <footer className="execution-media-actions">
        {!isReadMode && (saveError || (!upload.busy && upload.queue.length > 0)) && (
          <span className={`execution-upload-summary ${saveError ? 'execution-error-text' : ''}`}>
            {saveError ? (
              <>
                {t.saveFailed}
                <Button variant="ghost" onClick={deleteCurrent}>
                  {t.retry}
                </Button>
              </>
            ) : (
              `${upload.queue.length} ${t.failed}`
            )}
          </span>
        )}
        {mediaFooter}
      </footer>
      {(!embedded || currentUrl) && (
        <section className="execution-media-discussion">
          {currentUrl ? (
            <ResultCommments
              splitView
              composerTarget={composerTarget}
              onCommentOverlayOpenChange={setCommentOverlayOpen}
              subtask={subtask}
              retail_id_context_id={retail_id_context_id}
              recurrence_ts={recurrence_ts}
              resultContextId={resultContextId}
              mediaIndex={index}
              objectType={keys.COMMENT_OBJECT_TYPE.COMMENT_ON_IMAGE}
              media={media}
              tid={tid}
              isMediaLoading={false}
              imageToComment={imageToComment}
              onImageToCommentConsumed={() => setImageToComment(undefined)}
              isAIComment={false}
              targetParentCommentId={targetParentCommentId}
              targetCommentId={targetCommentId}
              analysisLoading={analysisLoading && index === media.length - 1}
              aiScore={score}
            />
          ) : (
            <div className="execution-discussion-empty">
              <MessageSquare size={26} />
              <strong>{t.selectHint}</strong>
            </div>
          )}
          {analysisError && (
            <div className="execution-analysis-error" role="alert">
              {t.aiFailed}
              <Button variant="ghost" className="execution-link" onClick={analyze}>
                {t.retryAnalysis}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
